import { Stack } from '@mui/material';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import debounce from 'lodash.debounce';
import PropTypes from 'prop-types';
import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Alert from '/@/components/bayer/alert';
import Campaign from '/@/components/carbon-calculator/campaign';
import CampaignForm from '/@/components/carbon-calculator/campaign-form';
import PerformanceField from '/@/components/carbon-calculator/campaign-performance-input';
import HeaderCarbonCalculator from '/@/components/carbon-calculator/header';
import ComparativeVerticalBarsGraph from '/@/components/comparative-vertical-bars-graph';
import Container from '/@/components/container';
import Snackbar from '/@/components/snackbar';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { useFieldById } from '/@/hooks/fields';
import { _ } from '/@/locale';
import {
    setAgriculturalPractice,
    setCrops,
    setSelectedCropRotation,
    setSelectedSoilTexture,
    setSoilTextures,
} from '/@/store/carbon-calculator-BR/actions';
import {
    getAgriculturalPractice,
    getAgriculturalPracticeOptions,
    getCropOptions,
    getCropRotation,
    getCropRotationSelected,
    getCropSummerOptions,
    getCrops,
    getCropWinterOptions,
    getSelectedSoilTexture,
    getSoilTextures,
    getSoilTexturesOptions,
} from '/@/store/carbon-calculator-BR/selectors';
import { axFetch, fetchHelper } from '/@/store/session/actions';
import { getUser } from '/@/store/session/selectors';
import { isPhone } from '/@/utils';
import Input from '/@/v3/components/forms/input';
import Select from '/@/v3/components/forms/select';
import ToggleGroup from '/@/v3/components/forms/toggle-group';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

const DEFAULT_CAMPAIGN = {
    winterPerformance: '',
    springPerformance: '',
    autumnPerformance: '',
    winterDryMaterial: 0,
    springDryMaterial: 0,
    autumnDryMaterial: 0,
    selectedSoilPreparation: [],
    selectedStrawMaintenance: [],
    winterSelectedCrop: 0,
    springSelectedCrop: 0,
    autumnSelectedCrop: 0,
    selectedAgriculturalPractice: 0,
    dryMaterial: '',
    soilPreparationError: false,
    stubbleMaintenanceError: false,
};

@connect(
    (state, props) => {
        return {
            soilTexture: getSoilTextures(state),
            soilTexturesOptions: getSoilTexturesOptions(state),
            selectedSoilTexture: getSelectedSoilTexture(state),
            crops: getCrops(state),
            cropOptions: getCropOptions(state),
            options: getCropRotation(state),
            selectedCropRotation: getCropRotationSelected(state),
            agriculturalPractice: getAgriculturalPractice(state),
            agriculturalPracticeOptions: getAgriculturalPracticeOptions(state),
            user: getUser(state),
            cropWinterOptions: getCropWinterOptions(state),
            cropSummerOptions: getCropSummerOptions(state),
        };
    },
    (dispatch) =>
        bindActionCreators(
            {
                setSoilTextures,
                setSelectedSoilTexture,
                setCrops,
                setAgriculturalPractice,
                setSelectedCropRotation,
            },
            dispatch
        )
)
@autobind
class CarbonCalculatorBR extends Component {
    static contextTypes = {
        history: PropTypes.object,
        units: PropTypes.object,
    };

    state = {
        area: '',
        campaigns: {
            1: DEFAULT_CAMPAIGN,
            2: DEFAULT_CAMPAIGN,
            3: DEFAULT_CAMPAIGN,
            4: DEFAULT_CAMPAIGN,
        },
        selectedCampaign: 1,
        location: '',
        municipality: '',
        UF: '',
        resultData: [],
        showSnackbar: false,
        inputError: false,
        selectError: false,
        cropRotationError: false,
        error: false,
    };

    componentDidMount() {
        this.fetchSoilTextures();
        this.fetchCrops();
        this.agriculturalPractice();
        this.getLocationAndUF();
    }

    componentWillUnmount() {
        this.props.setSelectedSoilTexture(undefined);
    }

    async fetchSoilTextures() {
        const url = `${settings.urls.carbonCalculator.soilTexture}`;
        const data = await fetchHelper(url, {
            method: 'get',
        });
        this.props.setSoilTextures(data.data.data);
    }

    async fetchCrops() {
        const url = `${settings.urls.carbonCalculator.cropsBr}`;
        const data = await fetchHelper(url, {
            method: 'get',
        });
        const formatedData = data.data.data.map((crop) => {
            return { ...crop, season: crop.cropName.split('.')[0] };
        });
        this.props.setCrops(formatedData);
    }

    async agriculturalPractice() {
        const url = `${settings.urls.carbonCalculator.agriculturalPractice}`;
        const data = await fetchHelper(url, {
            method: 'get',
        });
        this.props.setAgriculturalPractice(data.data.data);
    }

    round(value, digits) {
        const factor = 10 ** digits;
        value += Math.sign(value) * Number.EPSILON;
        return Math.round(value * factor) / factor;
    }

    debounceSnackbar = debounce(this.handleSnackbar, 1000);

    hideSnackBar() {
        setTimeout(() => this.setState({ showSnackbar: false }), 3000);
    }

    handleSnackbar() {
        this.setState({ showSnackbar: true });
    }

    validateFieldsToShowError() {
        if (this.state.area === '') {
            this.setState({ inputError: true });
        }

        if (this.props.selectedSoilTexture === undefined) {
            this.setState({ selectError: true });
        }

        if (!this.props.selectedCropRotation.length) {
            this.setState({ cropRotationError: true });
        }
    }

    setInputError(value) {
        if (this.state.area === '') {
            this.setState({ inputError: true });
        }

        if (this.props.selectedSoilTexture === undefined) {
            this.setState({ selectError: true });
        }
        this.props.setSelectedCropRotation(value);
        if (this.state.cropRotationError) {
            this.setState({ cropRotationError: false });
        }
        this.debounceValidateAndGetResult;
    }

    setArea(value) {
        this.setState({ area: value });
        if (this.state.inputError) {
            this.setState({ inputError: false });
        }
    }

    handleNoError() {
        if (this.state.selectError) {
            this.setState({ selectError: false });
        }
    }

    handleDeleteSeason(campaignId, key, season, seasonDryMaterial) {
        this.modifyCapaignValue(campaignId, key, 0);
        this.modifyCapaignValue(campaignId, season, '');
        this.modifyCapaignValue(campaignId, seasonDryMaterial, 0);
    }

    modifyCapaignValue(campaignId, key, value, showError) {
        this.setState((prevState) => ({
            ...prevState,
            campaigns: {
                ...prevState.campaigns,
                [campaignId]: {
                    ...prevState.campaigns[campaignId],
                    [key]: value,
                },
            },
        }));

        if (key === 'dryMaterial') {
            this.debounceSnackbar();
            this.hideSnackBar();
        }

        if (showError === 'showCropRotationError') {
            this.validateFieldsToShowError();
            if (!this.props.selectedCropRotation.length) {
                this.setState({ cropRotationError: true });
            }

            if (this.state.campaigns?.[campaignId]?.soilPreparationError && key === 'selectedSoilPreparation') {
                this.modifyCapaignValue(campaignId, 'soilPreparationError', false);
            }

            if (this.state.campaigns?.[campaignId]?.stubbleMaintenanceError && key === 'selectedStrawMaintenance') {
                this.modifyCapaignValue(campaignId, 'stubbleMaintenanceError', false);
            }
        }

        this.debounceValidateAndGetResult();
    }

    handleSelectCultura(campaignId, season, value) {
        this.modifyCapaignValue(campaignId, `${season}SelectedCrop`, value);
        this.modifyCapaignValue(campaignId, `${season}Performance`, '');
        this.validateFieldsToShowError();

        const crop = this.getCropIc(value);
        if (crop.cropIc === 0) {
            this.modifyCapaignValue(campaignId, `${season}DryMaterial`, crop.dryMass);
            this.debounceSnackbar();
            this.hideSnackBar();
        } else {
            this.modifyCapaignValue(campaignId, `${season}DryMaterial`, 0);
        }

        if (this.state.campaigns?.[campaignId]?.selectedSoilPreparation.length < 1) {
            this.modifyCapaignValue(campaignId, 'soilPreparationError', true);
        }

        if (this.state.campaigns?.[campaignId]?.selectedStrawMaintenance.length < 1) {
            this.modifyCapaignValue(campaignId, 'stubbleMaintenanceError', true);
        }
    }

    getCropIc(value) {
        return Object.values(this.props.crops).find((crop) => crop.id === value) || {};
    }

    handleScroll(id) {
        const element = document.getElementById(id);
        element.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }

    debounceScroll = debounce((id) => this.handleScroll(id), 50);

    handleOnClickCampaign(campaign) {
        this.setState({ selectedCampaign: campaign, showSnackbar: false });
        this.debounceScroll('scroll');
    }

    minimizeCampaign() {
        this.setState({ selectedCampaign: 0 });
    }

    calculateDryMaterial(campaign, season, value) {
        const selectedCrop = this.state.campaigns[campaign][`${season}SelectedCrop`];
        if (selectedCrop > 0) {
            const cropIc = this.getCropIc(selectedCrop);
            if (cropIc.cropIc > 0) {
                this.modifyCapaignValue(campaign, `${season}Performance`, value);
                const dryMaterial = value / cropIc.cropIc - value;
                this.modifyCapaignValue(campaign, `${season}DryMaterial`, this.round(dryMaterial, 3));
            } else {
                this.modifyCapaignValue(campaign, `${season}Performance`, cropIc.dryMass);
                this.modifyCapaignValue(campaign, `${season}DryMaterial`, 0);
            }
        }
        this.debounceSnackbar();
        this.hideSnackBar();
    }

    parseYear(campaignId) {
        const campaign = this.state.campaigns[campaignId];
        const cropsKeys = {
            winter_harvest: 'winter',
            summer_harvest: 'spring',
            off_season: 'autumn',
        };

        const parsedCampaign = {
            year: campaignId,
            preparation:
                typeof campaign.selectedSoilPreparation[0] === 'number'
                    ? !!campaign.selectedSoilPreparation[0]
                    : undefined,
            stubble:
                typeof campaign.selectedStrawMaintenance[0] === 'number'
                    ? !!campaign.selectedStrawMaintenance[0]
                    : undefined,
            crops: {},
            practice: {
                organic_fertilization: campaign.selectedAgriculturalPractice,
                production: Number(campaign.dryMaterial),
            },
        };

        Object.keys(cropsKeys).forEach((cropKey) => {
            const season = cropsKeys[cropKey];
            const parsedCrop = {
                crop_id: campaign[`${season}SelectedCrop`],
                production: Number(campaign[`${season}Performance`]),
            };

            if (parsedCrop.crop_id === 0) return;
            parsedCampaign.crops[cropKey] = parsedCrop;
        });

        return parsedCampaign;
    }

    parseData() {
        return {
            state: this.state.UF,
            municipality: this.state.location,
            area: Number(this.state.area),
            texture_id: this.props.selectedSoilTexture,
            crop_rotation: !!this.props.selectedCropRotation[0],
            years: Object.keys(this.state.campaigns).map(this.parseYear).filter(this.isValidYear),
        };
    }

    isValidYear(year) {
        if (!year.year || typeof year.preparation !== 'boolean' || typeof year.stubble !== 'boolean') {
            return false;
        }

        if (Object.keys(year.crops).length === 0) return false;
        const hasOneValidCrop = Object.keys(year.crops).every((cropKey) => {
            const crop = year.crops[cropKey];
            if (crop.crop_id === 0) return true;

            const cropIc = this.getCropIc(crop.crop_id);
            return (cropIc.cropIc > 0 && crop.production > 0) || cropIc.cropIc === 0;
        });
        return hasOneValidCrop;
    }

    isValidData() {
        const { state, municipality, area, texture_id, crop_rotation, years } = this.parseData();

        if (state && municipality && area > 0 && texture_id > 0 && typeof crop_rotation === 'boolean') {
            return years.length > 0;
        }
        return false;
    }

    validateAndGetResult() {
        if (this.isValidData()) this.getResult();
    }

    debounceValidateAndGetResult = debounce(this.validateAndGetResult, 600);

    async getResult() {
        const body = this.parseData();
        let url = `${settings.urls.carbonCalculator.calculateBr}`;

        const qparams = new URLSearchParams(this.context.history.location.search);
        const id = qparams.get('id');
        if (id) {
            body.id = id;
            url += `?id=${id}`;
        }
        let res;
        try {
            res = await axFetch.post(url, body);

            if (res.status !== 200) this.setState({ error: true });
        } catch (err) {
            this.setState({ error: true });
        }

        // Set ID to update DB results
        if (!id) {
            const newId = res.data.data.id;
            if (newId) this.context.history({ search: `id=${newId}` }, { replace: true });
        }

        const years = res.data.data.years.map((year) => ({
            ...year,
            c: {
                accumulated: year.c.accumulated
                    ? this.context.units.toWeightPerArea(year.c.accumulated, 2).value
                    : year.c.accumulated,
                differential: year.c.differential
                    ? this.context.units.toWeightPerArea(year.c.differential, 2).value
                    : year.c.differential,
            },
        }));

        let invalidYears = [1, 2, 3, 4];
        years.forEach((year) => {
            const yearId = Number(year.year);
            const yearIndex = invalidYears.indexOf(yearId);
            if (yearIndex > -1) invalidYears.splice(yearIndex, 1);
        });
        invalidYears = invalidYears.map((year) => ({
            year: String(year),
            c: {
                differential: 0,
                accumulated: 0,
            },
        }));

        const allYears = years.concat(invalidYears).sort((a, b) => Number(a.year) - Number(b.year));

        this.setState({ resultData: allYears });
    }

    getLocationAndUF() {
        const { field } = this.props;
        const location = field.location.split(', ');
        let UF = field.location.split(', ');
        UF = UF.find((el) => el.length === 2);
        this.setState({
            location: location.length === 4 ? location[1] : location[0],
            UF,
            municipality: location.length === 3 ? location[0] : `${location[0]}, ${location[1]}`,
        });
    }

    renderFieldData() {
        const areaUnit = this.context.units.getUnit('area');

        const { field } = this.props;
        return (
            <div className={styles.box}>
                <div className={styles.fieldData}>
                    <div className={styles.dataContainer}>
                        <p className={styles.fieldText}>{_('Dueño')}</p>
                        <p
                            className={`${styles.fieldDescription} mp-mask`}
                        >{`${this.props.user.first_name} ${this.props.user.last_name}`}</p>
                    </div>
                    <div className={styles.dataContainer}>
                        <p className={styles.fieldText}>{_('Nombre del lote')}</p>
                        <p className={`${styles.fieldDescription} mp-mask`}>{field.name}</p>
                    </div>
                    <div className={styles.dataContainer}>
                        <p className={styles.fieldText}>{_('Municipio')}</p>
                        <p className={`${styles.fieldDescription} mp-mask`}>{this.state.municipality}</p>
                    </div>
                    <div className={styles.dataContainer}>
                        <p className={styles.fieldText}>{_('Unidad Federativa')}</p>
                        <p className={styles.fieldDescription}>{this.state.UF}</p>
                    </div>
                </div>
                <div className={styles.fieldInput}>
                    <div className={styles.fieldInputData}>
                        <Input
                            type='number'
                            placeholder={_('Área del lote ({{unit}})', { unit: areaUnit })}
                            title={_('Área del lote ({{unit}})', { unit: areaUnit })}
                            onChange={(v) => {
                                this.setArea(this.context.units.fromArea(v));
                                this.debounceValidateAndGetResult();
                            }}
                            color={settings.app.theme}
                            extraClass={styles.widthInput}
                            titleStyles={styles.fieldText}
                            error={this.state.inputError ? _('Este campo es requerido.') : false}
                        />
                    </div>
                    <div className={styles.fieldInputData}>
                        <Select
                            value={this.props.selectedSoilTexture}
                            options={this.props.soilTexturesOptions}
                            placeholder={_('Seleccione una textura')}
                            title={_('Textura del suelo')}
                            onChange={(v) => {
                                this.props.setSelectedSoilTexture(v);
                                this.debounceValidateAndGetResult();
                                this.handleNoError();
                            }}
                            className={styles.selectCampaignDropdown}
                            color={settings.app.theme}
                            titleStyles={styles.fieldText}
                            error={this.state.selectError}
                            errorMsg={_('Este campo es requerido.')}
                        />
                    </div>
                </div>
            </div>
        );
    }

    renderResults() {
        return (
            <div>
                <Title size='md'>
                    {_(
                        'Elija la rotación de cultivos y cargue los datos del año. Puede cargar años anteriores o futuros.'
                    )}
                </Title>
                <div className={styles.resultsContainer}>
                    {this.renderCampaigns()}
                    <div className={styles.col}>
                        {this.renderCalculationResults()}
                        {this.renderGraphics()}
                    </div>
                </div>
            </div>
        );
    }

    renderDisclaimer() {
        return (
            <div className={styles.containerDisclaimer}>
                <Title className={styles.disclaimer}>
                    {_('Valores estimados considerando especificaciones de suelo  y clima de cada región')}
                </Title>
            </div>
        );
    }

    renderCampaigns() {
        return (
            <div className={styles.col} id='scroll'>
                <Campaign
                    type='check'
                    onChange={(val) => this.setInputError(val)}
                    error={this.state.cropRotationError}
                    selected={!this.state.cropRotationError}
                />
                {Object.keys(this.state.campaigns).map((campaign) => {
                    const winterCropIc = this.getCropIc(this.state.campaigns?.[campaign]?.winterSelectedCrop).cropIc;
                    const springCropIc = this.getCropIc(this.state.campaigns?.[campaign]?.springSelectedCrop).cropIc;
                    const autumnCropIc = this.getCropIc(this.state.campaigns?.[campaign]?.autumnSelectedCrop).cropIc;

                    const springDryMaterial = this.context.units.toWeightPerArea(
                        this.state.campaigns?.[campaign]?.springDryMaterial,
                        3
                    );
                    const autumnDryMaterial = this.context.units.toWeightPerArea(
                        this.state.campaigns?.[campaign]?.autumnDryMaterial,
                        3
                    );
                    const winterDryMaterial = this.context.units.toWeightPerArea(
                        this.state.campaigns?.[campaign]?.winterDryMaterial,
                        3
                    );

                    return (
                        <Campaign
                            key={campaign}
                            type='text'
                            number={campaign}
                            selected={this.state.selectedCampaign == campaign}
                            onClick={() => this.handleOnClickCampaign(campaign)}
                            minimizeCampaign={() => this.minimizeCampaign()}
                        >
                            {this.state.selectedCampaign == campaign && (
                                <Fragment>
                                    <CampaignForm
                                        title={_('A - Pilares de la sustentaibilidad agrícola')}
                                        error={
                                            this.state.campaigns?.[campaign]?.soilPreparationError ||
                                            this.state.campaigns?.[campaign]?.stubbleMaintenanceError
                                        }
                                    >
                                        <div className={styles.formTitles}>
                                            <Title className={styles.title} size='md'>
                                                {_('Preparación del suelo')}
                                            </Title>
                                            <ToggleGroup
                                                type='radio'
                                                boolean
                                                value={this.state.campaigns?.[campaign]?.selectedSoilPreparation}
                                                options={this.props.options}
                                                onChange={(value) =>
                                                    this.modifyCapaignValue(
                                                        campaign,
                                                        'selectedSoilPreparation',
                                                        value,
                                                        'showCropRotationError'
                                                    )
                                                }
                                                small
                                            />
                                        </div>
                                        <div className={styles.formTitles}>
                                            <Title className={styles.title} size='md'>
                                                {_('Mantenimiento del rastrojo')}
                                            </Title>
                                            <ToggleGroup
                                                type='radio'
                                                boolean
                                                value={this.state.campaigns?.[campaign]?.selectedStrawMaintenance}
                                                options={this.props.options}
                                                onChange={(value) =>
                                                    this.modifyCapaignValue(
                                                        campaign,
                                                        'selectedStrawMaintenance',
                                                        value,
                                                        'showCropRotationError'
                                                    )
                                                }
                                                small
                                            />
                                        </div>
                                    </CampaignForm>
                                    <CampaignForm title={_('B - Sistema de cultivo')}>
                                        <PerformanceField
                                            value={this.state.campaigns?.[campaign]?.springPerformance}
                                            onChange={(value) =>
                                                this.calculateDryMaterial(
                                                    campaign,
                                                    'spring',
                                                    this.context.units.fromWeightPerArea(value)
                                                )
                                            }
                                            valueDisabled={springCropIc === 0 || springCropIc === undefined}
                                            title={_('Cosecha (verano)')}
                                            options={this.props.cropSummerOptions}
                                            onChangeSelect={(value) =>
                                                this.handleSelectCultura(campaign, 'spring', value)
                                            }
                                            valueSelect={this.state.campaigns?.[campaign]?.springSelectedCrop}
                                            performance={`${springDryMaterial.value}(${springDryMaterial.unit})`}
                                            placeholder={_('Cultivo')}
                                            onClickBtn={() =>
                                                this.handleDeleteSeason(
                                                    campaign,
                                                    'springSelectedCrop',
                                                    'springPerformance',
                                                    'springDryMaterial'
                                                )
                                            }
                                            zIndex='four'
                                        />

                                        <PerformanceField
                                            value={this.state.campaigns?.[campaign]?.autumnPerformance}
                                            onChange={(value) =>
                                                this.calculateDryMaterial(
                                                    campaign,
                                                    'autumn',
                                                    this.context.units.fromWeightPerArea(value)
                                                )
                                            }
                                            valueDisabled={autumnCropIc === 0 || autumnCropIc === undefined}
                                            title={_('Cosecha (otoño)')}
                                            placeholder={_('Cultivo')}
                                            options={this.props.cropOptions}
                                            onChangeSelect={(value) =>
                                                this.handleSelectCultura(campaign, 'autumn', value)
                                            }
                                            valueSelect={this.state.campaigns?.[campaign]?.autumnSelectedCrop}
                                            performance={`${autumnDryMaterial.value}(${autumnDryMaterial.unit})`}
                                            onClickBtn={() =>
                                                this.handleDeleteSeason(
                                                    campaign,
                                                    'autumnSelectedCrop',
                                                    'autumnPerformance',
                                                    'autumnDryMaterial'
                                                )
                                            }
                                            zIndex='three'
                                        />
                                        <PerformanceField
                                            value={this.state.campaigns?.[campaign]?.winterPerformance}
                                            onChange={(value) =>
                                                this.calculateDryMaterial(
                                                    campaign,
                                                    'winter',
                                                    this.context.units.fromWeightPerArea(value)
                                                )
                                            }
                                            valueDisabled={winterCropIc === 0 || winterCropIc === undefined}
                                            title={_('Cosecha (invierno) o cultivo de cobertura')}
                                            placeholder={_('Cultivo')}
                                            options={this.props.cropWinterOptions}
                                            onChangeSelect={(value) =>
                                                this.handleSelectCultura(campaign, 'winter', value)
                                            }
                                            valueSelect={this.state.campaigns?.[campaign]?.winterSelectedCrop}
                                            performance={`${winterDryMaterial.value}(${winterDryMaterial.unit})`}
                                            onClickBtn={() =>
                                                this.handleDeleteSeason(
                                                    campaign,
                                                    'winterSelectedCrop',
                                                    'winterPerformance',
                                                    'winterDryMaterial'
                                                )
                                            }
                                            zIndex='two'
                                        />
                                    </CampaignForm>
                                    <CampaignForm title={_('C - Práctica agrícola complementaria')}>
                                        <PerformanceField
                                            value={this.state.campaigns?.[campaign]?.dryMaterial}
                                            onChange={(value) =>
                                                this.modifyCapaignValue(
                                                    campaign,
                                                    'dryMaterial',
                                                    this.context.units.fromWeightPerArea(value)
                                                )
                                            }
                                            title={_('Fertilización orgánica')}
                                            options={this.props.agriculturalPracticeOptions}
                                            onChangeSelect={(value) =>
                                                this.modifyCapaignValue(campaign, 'selectedAgriculturalPractice', value)
                                            }
                                            valueSelect={this.state.campaigns?.[campaign]?.selectedAgriculturalPractice}
                                            dryMaterial
                                            placeholder={_('Práctica Agrícola')}
                                            onClickBtn={() =>
                                                this.handleDeleteSeason(
                                                    campaign,
                                                    'selectedAgriculturalPractice',
                                                    'dryMaterial'
                                                )
                                            }
                                            zIndex='one'
                                        />
                                    </CampaignForm>
                                    {this.renderSnackBar(campaign)}
                                </Fragment>
                            )}
                        </Campaign>
                    );
                })}
            </div>
        );
    }

    renderCalculationResults() {
        const weightAreaUnit = this.context.units.getUnit('weight/area');
        const weightUnit = this.context.units.getUnit('weight');

        const years = this.state.resultData.filter((res) => res.c.accumulated !== 0).length;

        let accumulated = 0;
        let differential = 0;
        if (this.state.resultData) {
            differential = this.round(this.state.resultData.reduce((acc, el) => el.c.differential + acc, 0) / years, 2);
            accumulated = this.round((differential / 1000) * this.state.area, 2);
        }

        return (
            <div className={classNames(styles.carbonResultContainer, styles.carbonResult)}>
                <div className={styles.resultHeader}>
                    {/* TODO: cuando llegue el texto cargarlo con sus traducciones */}
                    {/* <Tooltip largeText text={''}> */}
                    <div className={styles.AC}>
                        <span>{_('∆C ({{unit}}/año)', { unit: weightAreaUnit })}</span>
                        {/* <i id={styles.icon} className={'material-icons'}>
                                info
                            </i> */}
                    </div>
                    {/* </Tooltip> */}
                    {/* <Tooltip largeText text={''}> */}
                    <div className={styles.acumulated}>
                        <span>{_('C acumulado ({{unit}}/año en la región)', { unit: weightUnit })}</span>
                        {/* <i id={styles.secondIcon} className={'material-icons'}>
                                info
                            </i> */}
                    </div>
                    {/* </Tooltip> */}
                </div>
                <div className={styles.results}>
                    {this.state.resultData.length > 0
                        ? this.state.resultData.map((year, i) => {
                              return (
                                  <div key={i} className={styles.resultRow}>
                                      <p>{year.c.differential === 0 ? '00,00' : this.round(year.c.differential, 2)}</p>
                                      <p>{year.c.accumulated === 0 ? '00,00' : this.round(year.c.accumulated, 2)}</p>
                                      <div className={styles.solo}>
                                          <Fragment>
                                              {_('suelo')}
                                              {year.c.differential !== 0 && (
                                                  <div className={year.c.differential > 0 ? styles.blue : styles.red} />
                                              )}
                                          </Fragment>
                                      </div>
                                  </div>
                              );
                          })
                        : Object.keys(this.state.campaigns).map(() => {
                              return (
                                  <div className={styles.resultRow}>
                                      <p>00,00</p>
                                      <p>00,00</p>
                                      <div className={styles.solo}>
                                          {_('suelo')}
                                          <div className={styles.empty} />
                                      </div>
                                  </div>
                              );
                          })}
                </div>
                <div className={styles.resultFooter}>
                    <div>
                        <p className={styles.totalTitle}>{_('∆C total anual')}</p>
                        <p className={styles.totalNumber}>
                            {differential !== 0 ? differential : '00'} {_('{{unit}}/año', { unit: weightAreaUnit })}
                        </p>
                    </div>
                    <div>
                        <p className={styles.totalTitle}> {_('C acumulado total anual')}</p>
                        <p className={styles.totalNumber}>
                            {accumulated !== 0 ? accumulated : '00,00'}{' '}
                            {_('{{unit}}/año en la región', { unit: weightUnit })}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    renderGraphics() {
        const unit = this.context.units.getUnit('weight/area');

        const RESULT_CONFIG = {
            label: _('Clase del área'),
            values: [
                {
                    value: 'A++++',
                    backgroundColor: '#243861',
                    color: 'white',
                },
                {
                    value: 'A+++',
                    backgroundColor: '#375591',
                    color: 'white',
                },
                {
                    value: 'A++',
                    backgroundColor: '#93a9d7',
                },
                {
                    value: 'A+',
                    backgroundColor: '#b7c6e4',
                },
                {
                    value: 'N',
                    backgroundColor: 'white',
                },
                {
                    value: 'B-',
                    backgroundColor: '#faddd6',
                },
                {
                    value: 'B--',
                    backgroundColor: '#dc7765',
                },
                {
                    value: 'B---',
                    backgroundColor: '#a52128',
                    color: 'white',
                },
                {
                    value: 'B----',
                    backgroundColor: '#7b211b',
                    color: 'white',
                },
            ],
        };

        const VALUES_CONFIG = {
            label: _('Rango de C ({{unit}}/año)', { unit }),
            values: [
                { min: 1750 },
                { min: 1251, max: 1750 },
                { min: 751, max: 1251 },
                { min: 251, max: 751 },
                { min: -250, max: 250 },
                { min: -751, max: -251 },
                { min: -1250, max: -751 },
                { min: -1750, max: -1251 },
                { max: -1750 },
            ],
        };

        VALUES_CONFIG.values = VALUES_CONFIG.values.map(({ min, max }) => {
            let value;
            if (min) min = this.context.units.toWeightPerArea(min, 0).value;
            if (max) max = this.context.units.toWeightPerArea(max, 0).value;

            if (max === undefined) {
                value = `> ${min}`;
            } else if (min === undefined) {
                value = `< ${max}`;
            } else {
                value = `${min} a ${max}`;
            }

            return { min, max, value };
        });

        let differential;
        if (this.state.resultData.length) {
            differential = this.round(
                this.state.resultData.reduce((acc, el) => el.c.differential + acc, 0),
                2
            );
        }

        const selectedIndex = VALUES_CONFIG.values.findIndex(
            (item) => (differential >= item.min || !item.min) && (differential <= item.max || !item.max)
        );

        return (
            <div className={classNames(styles.carbonResultContainer, styles.carbonGraph)}>
                <ComparativeVerticalBarsGraph
                    barConfig1={RESULT_CONFIG}
                    barConfig2={VALUES_CONFIG}
                    direction='column'
                    selectedIndex={selectedIndex}
                />
            </div>
        );
    }

    renderSnackBar(campaign) {
        let lastResult = {};
        if (this.state.resultData.length !== 0) {
            lastResult = this.state.resultData[campaign - 1];
        }
        return (
            <div>
                {isPhone() && this.state.resultData.length !== 0 && this.state.showSnackbar && (
                    <Snackbar
                        children={
                            <div className={styles.snackBar}>
                                <div className={styles.titleWrapper}>
                                    <Title className={styles.snackBarText}>{_('∆C (kg/ha/año)')}</Title>
                                    <Title className={styles.snackBarTitle}>
                                        {this.round(lastResult.c.differential, 2)}
                                    </Title>
                                </div>
                                <div className={styles.titleWrapper}>
                                    <Title className={styles.snackBarText}>
                                        {_('C acumulado (t/año en la región)')}
                                    </Title>
                                    <Title className={styles.snackBarTitle}>
                                        {this.round(lastResult.c.accumulated, 3)}
                                    </Title>
                                </div>
                                {lastResult.c.differential > 0 ? (
                                    <div className={styles.greenSnackBar} />
                                ) : (
                                    <div className={styles.redSnackBar} />
                                )}
                            </div>
                        }
                        showSnackbar={this.state.showSnackbar}
                    />
                )}
            </div>
        );
    }

    render() {
        return (
            <div className={styles.root}>
                <Container classNameRoot={styles.containerBackground}>
                    <HeaderCarbonCalculator
                        descriptionText={_('Cargue los datos para poder calcular el carbono fijado.')}
                        textAlign='BR'
                        title={_('Estimativa de carbono fijado')}
                        firstParagraph={_(
                            'Te ayudamos a calcular el carbono fijado de acuerdo a tu rotación de cultivos.'
                        )}
                        secondParagraph={_('Para ver más información, poder acceder a la ')}
                    />
                    {this.renderFieldData()}
                    {this.state.error && (
                        <div className={styles.alert}>
                            <Alert sx={{ alignItems: 'center' }} severity='danger'>
                                {_('Ha ocurrido un error...')}
                            </Alert>
                        </div>
                    )}

                    {this.renderDisclaimer()}
                    {this.renderResults()}
                </Container>
            </div>
        );
    }
}

const CarbonCalculatorBRWrapper = (props: any) => {
    const fieldQuery = useFieldById({ id: props.fieldId }, { enabled: props.fieldId });
    if (fieldQuery.isValidating)
        return (
            <Stack justifyContent='center' alignItems='center'>
                <Spinner />;
            </Stack>
        );
    return <CarbonCalculatorBR {...props} field={fieldQuery.data} />;
};
export default CarbonCalculatorBRWrapper;
